import { createContext, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Admin Preview Mode — purely frontend, read-only. Stores preview state in
 * localStorage so the gold banner persists across route changes. Does NOT
 * impersonate the user on the backend — admin remains logged in as admin.
 *
 * Public API:
 *   const { preview, enter, exit, blockAction } = useAdminPreview()
 *   <PreviewBanner />                       — renders the gold banner
 *   <PreviewModeProvider>                   — wrap app root
 *   <PreviewAsLink … />                     — inline gold "Preview as …" row link
 *   <ViewProfileLink … />                   — inline muted "View Profile" row link
 */

const LS_KEY = "habano.admin_preview";
const AdminPreviewCtx = createContext({
  preview: null, enter: () => {}, exit: () => {}, blockAction: () => false,
});

function readLS() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
}
function writeLS(v) {
  try { v ? localStorage.setItem(LS_KEY, JSON.stringify(v)) : localStorage.removeItem(LS_KEY); } catch { /* no-op */ }
}

const DEST_FOR = {
  user: "/",
  traveler: "/",
  vendor: "/vendor",
  business: "/business",
  // Custom-location creator preview lands on the creator's My Places detail
  custom_location: (id) => `/my-places/custom/${id}`,
};

export function PreviewModeProvider({ children }) {
  const [preview, setPreview] = useState(readLS());
  // Sync across tabs.
  useEffect(() => {
    const onStorage = (e) => { if (e.key === LS_KEY) setPreview(readLS()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const enter = (account) => {
    const payload = {
      account_type: account.account_type,
      user_id: account.user_id,
      // Resource id for business/vendor/custom_location previews — the
      // doc id (not the owner user id). Used by the axios interceptor to
      // inject ?as_business_id / ?as_vendor_id on dashboard requests.
      resource_id: account.resource_id || account.id || null,
      display_name: account.display_name || account.full_name || account.name || "Account",
      return_to: account.return_to || window.location.pathname,
      started_at: new Date().toISOString(),
    };
    writeLS(payload);
    setPreview(payload);
    // Fire-and-forget audit log entry
    api.post("/admin/preview-log", {
      account_type: payload.account_type,
      target_id: payload.resource_id || payload.user_id,
      display_name: payload.display_name,
    }).catch(() => { /* silent — preview still works */ });
  };
  const exit = () => { writeLS(null); setPreview(null); };
  const blockAction = (label) => {
    if (preview) {
      toast.message(`Admin preview — actions disabled${label ? ` (${label})` : ""}`);
      return true;
    }
    return false;
  };
  return (
    <AdminPreviewCtx.Provider value={{ preview, enter, exit, blockAction }}>
      {children}
    </AdminPreviewCtx.Provider>
  );
}

export function useAdminPreview() {
  return useContext(AdminPreviewCtx);
}

export function PreviewBanner() {
  const { preview, exit } = useAdminPreview();
  const navigate = useNavigate();
  if (!preview) return null;
  const onExit = () => {
    const back = preview.return_to || "/admin";
    exit();
    navigate(back, { replace: true });
  };
  return (
    <div
      data-testid="admin-preview-banner"
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0,
        zIndex: 9999,
        background: "linear-gradient(90deg, #E8B84B 0%, #b9912f 100%)",
        color: "#0B0B0B",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        boxShadow: "0 2px 12px rgba(0,0,0,0.6)",
      }}
    >
      <Eye className="w-4 h-4" strokeWidth={2} />
      <span className="flex-1">
        <strong>Admin Preview</strong> — viewing as {preview.display_name}
        <span className="opacity-70 ml-2 text-[11px]">({preview.account_type})</span>
      </span>
      <button
        type="button"
        data-testid="admin-preview-exit"
        onClick={onExit}
        style={{
          background: "#0B0B0B",
          color: "#E8B84B",
          padding: "6px 14px",
          borderRadius: 999,
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          minHeight: 32,
        }}
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} /> Exit Preview
      </button>
    </div>
  );
}

/** Spacer that pushes content below the fixed banner when preview is on. */
export function PreviewBannerSpacer() {
  const { preview } = useAdminPreview();
  if (!preview) return null;
  return <div style={{ height: 44 }} aria-hidden="true" />;
}

/**
 * Compact gold-outline "Preview as …" button to drop into admin profile
 * headers (user/vendor/business detail pages).
 */
export function PreviewAsButton({ account, accountType, returnTo }) {
  const { enter } = useAdminPreview();
  const navigate = useNavigate();
  const label = account?.display_name || account?.full_name || account?.name || "Account";
  return (
    <button
      type="button"
      data-testid="preview-as-btn"
      onClick={() => {
        enter({
          account_type: accountType,
          user_id: account.user_id || account.id,
          resource_id: account.id || account.user_id,
          display_name: label,
          return_to: returnTo || window.location.pathname,
        });
        const dest = typeof DEST_FOR[accountType] === "function"
          ? DEST_FOR[accountType](account.id || account.user_id)
          : DEST_FOR[accountType] || "/";
        navigate(dest);
      }}
      className="inline-flex items-center gap-1.5 text-gold text-[12px] uppercase tracking-widest"
      style={{ border: "1px solid rgba(232,184,75,0.4)", borderRadius: 999, padding: "6px 14px", minHeight: 36 }}
    >
      <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Preview as {label}
    </button>
  );
}

/**
 * Inline row-level link "Preview as [Role]" — gold eye icon, 44px tap target.
 * Used inside admin tables/lists.
 */
export function PreviewAsLink({ account, accountType, label, returnTo, testid }) {
  const { enter } = useAdminPreview();
  const navigate = useNavigate();
  const display = label || account?.display_name || account?.full_name || account?.name || "Account";
  const role = ({
    user: "User", traveler: "User",
    business: "Owner", vendor: "Vendor",
    custom_location: "Creator",
  })[accountType] || "User";
  return (
    <button
      type="button"
      data-testid={testid || `preview-as-${accountType}`}
      onClick={(e) => {
        e.stopPropagation();
        enter({
          account_type: accountType,
          user_id: account.user_id || account.id,
          resource_id: account.id || account.user_id,
          display_name: display,
          return_to: returnTo || window.location.pathname,
        });
        const dest = typeof DEST_FOR[accountType] === "function"
          ? DEST_FOR[accountType](account.id || account.user_id)
          : DEST_FOR[accountType] || "/";
        navigate(dest);
      }}
      title={`Preview as ${display}`}
      className="inline-flex items-center gap-1.5 text-[12px] font-medium"
      style={{
        color: "#E8B84B",
        border: "1px solid rgba(232,184,75,0.4)",
        borderRadius: 999,
        padding: "8px 12px",
        minHeight: 44,
        whiteSpace: "nowrap",
      }}
    >
      <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span>Preview as {role}</span>
    </button>
  );
}

/**
 * Inline row-level link "View Profile" / "View Public Page" — muted icon-link
 * that opens the public/un-authenticated view in the same tab. No preview banner.
 */
export function ViewProfileLink({ to, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid || "view-profile-link"}
      onClick={(e) => e.stopPropagation()}
      title={label || "View profile"}
      className="inline-flex items-center gap-1.5 text-[12px]"
      style={{
        color: "#9A9490",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 999,
        padding: "8px 12px",
        minHeight: 44,
        whiteSpace: "nowrap",
      }}
    >
      <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span>{label || "View Profile"}</span>
    </Link>
  );
}
